import { PalmettoTextSkeleton, PalmettoToaster } from '@palmetto/dsm-components';
import { Themes } from '@palmetto/dsm-tokens';
import { Utility } from '../../api/properties/types';
import useProperties from '../../api/properties/useProperties';
import Form from '../_forms/_uncontrolled/Form/Form';
import { Button, ButtonProps, RadioGroup } from '@helios/frontend-core';
import { DataCollection } from '../DataCollection/DataCollection';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from '@helios/frontend-core';

export interface UtilitySelectionWidgetProps {
  propertyId: string;
  isLoading?: boolean;
  utilities: Utility[];
  primaryCTA: Omit<ButtonProps, 'emphasis' | 'onClick'> & {
    onClick: (args: { utility?: Utility }) => void;
  };
  secondaryCTA?: Omit<ButtonProps, 'emphasis'>;
  utilityNotListedOnClick: () => void;
  theme?: keyof typeof Themes;
  trackingId: string;
}

export function UtilitySelectionWidget({
  propertyId,
  utilities,
  primaryCTA: { onClick: primaryCtaOnClick, ...restPrimaryCTA },
  secondaryCTA,
  utilityNotListedOnClick,
  theme,
  isLoading,
  trackingId,
}: UtilitySelectionWidgetProps) {
  const { updateProperty, isUpdatePropertyLoading, isUpdatePropertySuccess, error } = useProperties(propertyId);
  const { t } = useTranslation();

  type FormValues = {
    electricUtility: string;
  };

  const form = useForm<FormValues>();

  const {
    formState: { errors: formErrors },
  } = form;

  const onSubmit = async (formValues: FormValues) => {
    const value = formValues.electricUtility;
    if (value) {
      updateProperty({ electricUtility: value });
    } else {
      form.setError('electricUtility', {
        type: 'manual',
        message: t('common.forms.validation.required'),
      });
    }
  };

  useEffect(() => {
    const selectedUtilityId = form.getValues('electricUtility');
    if (isUpdatePropertySuccess && selectedUtilityId) {
      const selectedUtility = utilities?.find((utility) => utility._id === selectedUtilityId);
      primaryCtaOnClick({ utility: selectedUtility });
    }
  }, [isUpdatePropertySuccess]);

  useEffect(() => {
    if (error) {
      PalmettoToaster.error({
        title: t('onboarding.toasts.genericError'),
      });
    }
  }, [error]);

  const onRadioInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (formErrors.electricUtility) {
      form.clearErrors('electricUtility');
    }
    form.setValue('electricUtility', event.target.value);
  };

  return (
    <DataCollection
      heading={t('utilitySelectionWidget.heading')}
      descr={t('utilitySelectionWidget.descr')}
      theme={theme}
    >
      {isLoading ? (
        <div className="flex flex-col gap-16">
          <PalmettoTextSkeleton width="100%" typographyClass="heading-palmetto-800" />
          <PalmettoTextSkeleton width="100%" typographyClass="heading-palmetto-800" />
          <PalmettoTextSkeleton width="100%" typographyClass="heading-palmetto-800" />
        </div>
      ) : (
        <Form methods={form} className="flex flex-col gap-12" onSubmit={onSubmit} trackingId={'utility-selection'}>
          <RadioGroup
            trackingId="utility-selection-electric-utility"
            name="electricUtility"
            value={form.watch('electricUtility')?.toString()}
            label={t('utilitySelectionWidget.optionGroupLabel')}
            onChange={onRadioInputChange}
            options={
              utilities?.map((utility) => ({
                id: `${utility._id}-option`,
                label: utility.name,
                value: utility._id,
              })) || []
            }
            required
            hideRequiredIndicator
            disabled={isUpdatePropertyLoading}
            validationMessage={
              formErrors.electricUtility?.message
                ? { text: formErrors.electricUtility?.message, type: 'error' }
                : undefined
            }
          />
          <div className="flex flex-col gap-10">
            <Button
              type="button"
              disabled={isUpdatePropertyLoading}
              emphasis="text"
              className="m-auto"
              onClick={utilityNotListedOnClick}
              trackingId="utility-selection-provider-not-listed"
            >
              {t('utilitySelectionWidget.energyProviderNotListed')}
            </Button>
            <div className="flex flex-col gap-12">
              <Button type="submit" loading={isUpdatePropertyLoading} {...restPrimaryCTA}>
                {restPrimaryCTA.children}
              </Button>
              {secondaryCTA && (
                <Button type="button" emphasis="secondary" disabled={isUpdatePropertyLoading} {...secondaryCTA}>
                  {secondaryCTA.children}
                </Button>
              )}
            </div>
          </div>
        </Form>
      )}
    </DataCollection>
  );
}

export default UtilitySelectionWidget;
